import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationSoundService } from './notification-sound.service';

/** The lengths offered, in minutes — the same list the server accepts (NOTIFICATION_MUTE_MINUTES). */
export const MUTE_OPTIONS: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 10, label: '10 minutes' },
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 720, label: '12 hours' },
];

/** localStorage: the last mute state seen, so the button shows at once on the next start. */
const CACHE_KEY = 'link-notifications-mute';
/** Coming back to the app reloads the state at most this often. */
const REFRESH_GAP_MS = 60 * 1000;

/** "4:30 pm", or "tomorrow 4:30 am" when the time falls after today. */
export function muteEndText(ms: number): string {
  if (!ms) return '';
  const end = new Date(ms);
  const time = end.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return end.toDateString() === new Date().toDateString() ? time : `tomorrow ${time}`;
}

/** What is left, for the icon: "29m", then whole hours ("2h", "12h"). */
export function muteLeftText(ms: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.ceil((ms - now) / 60000));
  return minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`;
}

function readCache(): { until: number; known: boolean } {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return { until: Number(raw?.until) || 0, known: !!raw?.known };
  } catch {
    return { until: 0, known: false };
  }
}

function writeCache(until: number, known: boolean): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ until, known }));
  } catch { /* private mode: the state is simply reloaded next time */ }
}

const toMs = (iso: unknown): number => {
  const ms = iso ? new Date(String(iso)).getTime() : 0;
  return Number.isFinite(ms) && ms > Date.now() ? ms : 0;
};

/**
 * Mute (30 Oct 2026, founder): pause every notification for 10 minutes to 12 hours from the
 * header. The server holds the mute for the person, so no push reaches any of their devices
 * until it ends (`PUT /users/notifications/mute`); this service mirrors it on this device — the
 * button's state and time left, and no chime or pop-up here while it runs. Notifications still
 * collect under the bell. A server that does not know the mute yet (404) hides the button.
 */
@Injectable({ providedIn: 'root' })
export class NotificationMuteService {
  private readonly http = inject(HttpClient);
  private readonly zone = inject(NgZone);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sound = inject(NotificationSoundService);
  private readonly url = environment.host + 'users/notifications/mute';
  private readonly cached = readCache();

  /** Until when notifications are muted (ms since 1970); 0 when they are not. */
  readonly mutedUntil = signal(this.cached.until > Date.now() ? this.cached.until : 0);
  /** The server answers the mute routes; until it has, the button stays hidden. */
  readonly supported = signal(this.cached.known);
  /** A change is on its way to the server. */
  readonly busy = signal(false);
  /** Moves while muted, so the time left and the end of the mute show without a reload. */
  private readonly now = signal(Date.now());
  readonly muted = computed(() => this.mutedUntil() > this.now());

  private started = false;
  private lastLoad = 0;
  private ticker: ReturnType<typeof setInterval> | null = null;

  /** The header calls this: load the state, and again on coming back to the app (at most once a minute). */
  start(): void {
    this.apply(this.mutedUntil());
    if (this.started) return;
    this.started = true;
    void this.load();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && Date.now() - this.lastLoad > REFRESH_GAP_MS) void this.load();
    });
  }

  async load(): Promise<void> {
    this.lastLoad = Date.now();
    try {
      const res = await firstValueFrom(this.http.get<{ data?: { mutedUntil?: string | null } }>(this.url));
      this.zone.run(() => {
        this.supported.set(true);
        this.apply(toMs(res?.data?.mutedUntil));
      });
    } catch (error) {
      if ((error as HttpErrorResponse)?.status === 404)
        this.zone.run(() => {
          this.supported.set(false);
          this.apply(0);
        });
    }
  }

  /** Mute for `minutes` (one of MUTE_OPTIONS), or 0 to unmute; false when the server refused or could not be reached. */
  async set(minutes: number): Promise<boolean> {
    if (this.busy()) return false;
    this.busy.set(true);
    try {
      const res = await firstValueFrom(this.http.put<{ data?: { mutedUntil?: string | null } }>(this.url, { minutes }));
      this.apply(toMs(res?.data?.mutedUntil));
      return true;
    } catch (error) {
      const readOnly = (error as HttpErrorResponse)?.status === 403;
      this.snackBar.open(
        readOnly ? "This is a read-only view: notifications can't be changed here." : "Couldn't change notifications — check the connection and try again.",
        'OK',
        { duration: 4000 },
      );
      return false;
    } finally {
      this.busy.set(false);
    }
  }

  private apply(until: number): void {
    const ms = until > Date.now() ? until : 0;
    this.mutedUntil.set(ms);
    this.now.set(Date.now());
    writeCache(ms, this.supported());
    this.sound.setPausedUntil(ms);
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    if (!ms) return;
    this.zone.runOutsideAngular(() => {
      this.ticker = setInterval(() => {
        this.zone.run(() => {
          if (this.mutedUntil() <= Date.now()) this.apply(0);
          else this.now.set(Date.now());
        });
      }, 15000);
    });
  }
}
